import { useFormContext } from "react-hook-form";

import { usePasswordValidation } from "@auth0/auth0-acul-react/signup-password";
import type { PasswordValidationResult } from "@auth0/auth0-acul-react/types";

import RequiredFooter from "@/components/RequiredFooter";
import { ULThemeButton } from "@/components/ULThemeButton";
import { ULThemeFormMessage } from "@/components/form/ULThemeFormMessage";
import { FormField, FormItem } from "@/components/ui/form";
import { ULThemePasswordField } from "@/components/ULThemePasswordField";
import { ULThemePasswordValidator } from "@/components/ULThemePasswordValidator";

import locales from "../../locales/en.json";
import { PASSWORD_FIELDS, type WizardFormValues } from "./types";

interface SetPasswordStepProps {
  onContinue: () => void;
  onBack: () => void;
  // Flow B has no "Check details" review step, so this is its final sub-step
  // and the primary button submits the signup instead of advancing. Flow A
  // leaves both false and is unchanged.
  isSubmit?: boolean;
  isSubmitting?: boolean;
  // Bot-detection widget, rendered only when this is the flow's terminal
  // sub-step. Owned by SignupPasswordForm so both terminal steps share one
  // instance of the captcha wiring.
  captchaSlot?: React.ReactNode;
  blockPrimary?: boolean;
}

function SetPasswordStep({
  onContinue,
  onBack,
  isSubmit = false,
  isSubmitting = false,
  captchaSlot = null,
  blockPrimary = false,
}: SetPasswordStepProps) {
  const form = useFormContext<WizardFormValues>();
  const { watch, trigger } = form;

  const passwordValue = watch("password");
  const { isValid: isPasswordValid, results: passwordResults }: PasswordValidationResult =
    usePasswordValidation(passwordValue);

  const handleContinue = async () => {
    const valid = await trigger(PASSWORD_FIELDS);
    if (valid) onContinue();
  };

  return (
    <div>
      <FormField
        control={form.control}
        name="password"
        rules={{
          required: locales.form.fields.password.required,
          validate: (value) => {
            if (!value) return locales.form.fields.password.required;
            if (!isPasswordValid) return locales.form.fields.password.doesNotMeetRequirements;
            return true;
          },
        }}
        render={({ field, fieldState }) => (
          <FormItem>
            <ULThemePasswordField
              {...field}
              label={locales.form.fields.password.label}
              error={!!fieldState.error}
              autoFocus
            />
            <ULThemeFormMessage hasFormError={!!fieldState.error} />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="confirmPassword"
        rules={{
          required: locales.form.fields.confirmPassword.required,
          validate: (value) => value === watch("password") || locales.form.fields.confirmPassword.mismatch,
        }}
        render={({ field, fieldState }) => (
          <FormItem>
            <ULThemePasswordField
              {...field}
              label={locales.form.fields.confirmPassword.label}
              error={!!fieldState.error}
            />
            <ULThemeFormMessage hasFormError={!!fieldState.error} />
          </FormItem>
        )}
      />

      <ULThemePasswordValidator
        validationRules={passwordResults}
        passwordSecurityText={locales.form.passwordSecurity}
        show={!!passwordValue}
        className="mb-4"
      />

      {captchaSlot}

      <div className="flex gap-3">
        <ULThemeButton
          type="button"
          variant="outline"
          className="flex-1"
          onClick={onBack}
        >
          {locales.form.backButton}
        </ULThemeButton>
        <ULThemeButton
          type="button"
          className="flex-1"
          onClick={handleContinue}
          disabled={isSubmitting || blockPrimary}
        >
          {isSubmit ? locales.form.joinButton : locales.form.continueButton}
        </ULThemeButton>
      </div>
      <RequiredFooter />
    </div>
  );
}

export default SetPasswordStep;
